import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useBanner } from "@/context/BannerContext";
import { useAuthContext } from "@/lib/AuthProvider";
import { useLocation } from "react-router-dom";

interface MobileStickyHamburgerProps {
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
}

const MobileStickyHamburger = ({ 
  mobileMenuOpen, 
  setMobileMenuOpen 
}: MobileStickyHamburgerProps) => {
  const { bannerConfig, isBannerDismissed } = useBanner();
  const { user, isGoogleUser } = useAuthContext();
  const location = useLocation();
  const [topPosition, setTopPosition] = useState(16);

  // Calculate the top position accounting for banners
  const calculateTopPosition = () => {
    // Listen for the same banner height updates that the navbar uses
    const customNavHeight = parseInt(
      document.documentElement.style.getPropertyValue('--mobile-nav-height') || '0'
    );
    
    // Base offset from top
    return Math.max(customNavHeight, 0) + 16;
  };

  // Listen for height updates
  useEffect(() => {
    const handleNavHeightChange = () => {
      setTopPosition(calculateTopPosition());
    };

    // Initial calculation
    handleNavHeightChange();

    // Listen for updates
    window.addEventListener('navHeightUpdate', handleNavHeightChange);
    
    return () => {
      window.removeEventListener('navHeightUpdate', handleNavHeightChange);
    };
  }, []);

  // Check if current page is home page
  const isHomePage = location.pathname === "/" || location.pathname === "/home";

  // Only show on mobile and when not on home page (since home page will have full navbar)
  if (isHomePage) return null;

  return (
    <div 
      className="mobile-sticky-hamburger fixed z-[60] transition-all duration-300"
      style={{ 
        top: `${topPosition}px`,
        left: '16px'
      }}
    >
      <Button
        variant="ghost"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="relative group bg-white/95 backdrop-blur-md hover:bg-jamaica-green/10 
                   transition-all duration-300 rounded-full w-14 h-14 p-0
                   shadow-lg hover:shadow-xl border-2 border-white/50
                   hover:border-jamaica-green/30 hover:scale-110 active:scale-95
                   transform-gpu"
        size="icon"
      >
        {/* Background glow effect */}
        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-jamaica-green/20 to-jamaica-green/10 
                        blur-lg transform scale-150 opacity-0 group-hover:opacity-100 
                        transition-opacity duration-300 pointer-events-none" />
        
        {/* Animated border accent */}
        <div className="absolute inset-0 rounded-full border-2 border-jamaica-yellow/20 
                        opacity-0 group-hover:opacity-100 transition-opacity duration-300 
                        animate-pulse pointer-events-none" />
        
        {/* Icon */}
        <div className="relative z-10">
          {mobileMenuOpen ? (
            <X className="h-6 w-6 text-gray-700 group-hover:text-jamaica-green 
                           transition-colors duration-200" />
          ) : (
            <Menu className="h-6 w-6 text-gray-700 group-hover:text-jamaica-green 
                             transition-colors duration-200" />
          )}
        </div>
        
        {/* Subtle inner shadow for depth */}
        <div className="absolute inset-0 rounded-full shadow-inner pointer-events-none" />
      </Button>
    </div>
  );
};

export default MobileStickyHamburger;